import { useEffect, useState, type ReactNode } from 'react';
import { Search } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  Input,
  SegmentedControl,
  Select,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';
import { formatFixed } from '@/logic/statistics';
import type { MatchingMode, PointPair, SystemPoint } from '@/types';

interface MatchingReviewProps {
  pairs: PointPair[];
  system1Points: SystemPoint[];
  system2Points: SystemPoint[];
  method?: MatchingMode;
  replacedCount: number;
  skippedRows?: number;
  editable?: boolean;
  /**
   * `dialog` (default) asks for a modal decision before replacing pairs;
   * `inline` shows the first proposal in the workspace when no pairs exist.
   */
  presentation?: 'dialog' | 'inline';
  onAccept: (pairs: PointPair[]) => void;
  onCancel: () => void;
}

const REASONS: Record<MatchingMode, string> = {
  auto: 'Suggested match',
  exact: 'Identical identifier',
  offset: 'Numeric offset',
  prefix: 'Numeric prefix/suffix',
  nearest: 'Nearest available point',
};

type View = 'pairs' | 'unmatched';

/** Inspect a proposed replacement before committing new pairs or resetting weights. */
export const MatchingReview = ({
  pairs: initialPairs,
  system1Points,
  system2Points,
  method,
  replacedCount,
  skippedRows = 0,
  editable = true,
  presentation = 'dialog',
  onAccept,
  onCancel,
}: MatchingReviewProps) => {
  const [query, setQuery] = useState('');
  const [view, setView] = useState<View>('pairs');
  const [pairs, setPairs] = useState(initialPairs);
  const [correctedIds, setCorrectedIds] = useState<number[]>([]);
  useEffect(() => {
    setPairs(initialPairs);
    setCorrectedIds([]);
  }, [initialPairs]);

  const correctPoint = (
    id: number,
    side: 'record' | 'field',
    identifier: string
  ) => {
    const point = (side === 'record' ? system1Points : system2Points).find(
      (candidate) => candidate.point === identifier
    );
    if (!point) return;
    setPairs((current) =>
      current.map((pair) =>
        pair.id !== id
          ? pair
          : {
              ...pair,
              ...(side === 'record'
                ? {
                    sys1Point: point.point,
                    label: point.point,
                    n1: point.north,
                    e1: point.east,
                    desc1: point.description,
                  }
                : {
                    sys2Point: point.point,
                    n2: point.north,
                    e2: point.east,
                    desc2: point.description,
                  }),
              matchDistance: undefined,
              detectedOffset: undefined,
            }
      )
    );
    setCorrectedIds((current) =>
      current.includes(id) ? current : [...current, id]
    );
  };

  const recordUsed = new Set(pairs.map((p) => p.sys1Point));
  const fieldUsed = new Set(pairs.map((p) => p.sys2Point));
  const unmatched = [
    ...system1Points
      .filter((p) => !recordUsed.has(p.point))
      .map((p) => ({ ...p, side: 'Record' as const })),
    ...system2Points
      .filter((p) => !fieldUsed.has(p.point))
      .map((p) => ({ ...p, side: 'Field' as const })),
  ];
  const unmatchedRecord = unmatched.filter((p) => p.side === 'Record').length;
  const unmatchedField = unmatched.length - unmatchedRecord;
  const reused = new Set(
    pairs
      .filter((p) =>
        pairs.some(
          (other) =>
            other.id !== p.id &&
            (other.sys1Point === p.sys1Point || other.sys2Point === p.sys2Point)
        )
      )
      .map((p) => p.id)
  );
  const candidates = (point: string) => {
    const digits = point.replace(/\D/g, '');
    if (!digits) return [];
    return system2Points.filter((p) => {
      const other = p.point.replace(/\D/g, '');
      return other && (digits.endsWith(other) || other.endsWith(digits));
    });
  };
  const ambiguous = new Set(
    method === 'prefix'
      ? pairs.filter((p) => candidates(p.sys1Point).length > 1).map((p) => p.id)
      : []
  );
  const needle = query.trim().toLowerCase();
  const matches = (value: string) => value.toLowerCase().includes(needle);
  const shownPairs = pairs.filter((p) =>
    matches(`${p.sys1Point} ${p.sys2Point}`)
  );
  const shownUnmatched = unmatched.filter((p) =>
    matches(`${p.side} ${p.point} ${p.description ?? ''}`)
  );

  const reasonFor = (pair: PointPair) => {
    if (correctedIds.includes(pair.id)) return 'Corrected during review';
    if (method === 'nearest')
      return `${REASONS.nearest} (${formatFixed(pair.matchDistance, 3)} apart)`;
    if (method === 'offset')
      return `${REASONS.offset} ${pair.detectedOffset ?? ''}`.trim();
    return method ? REASONS[method] : 'Pair-file row';
  };

  const pointSelect = (
    pair: PointPair,
    side: 'record' | 'field'
  ): ReactNode => {
    const points = side === 'record' ? system1Points : system2Points;
    const value = side === 'record' ? pair.sys1Point : pair.sys2Point;
    if (!editable)
      return (
        <span
          className={
            side === 'record'
              ? 'font-mono font-medium text-record'
              : 'font-mono text-field'
          }
        >
          {value}
        </span>
      );
    return (
      <Select
        controlSize="sm"
        aria-label={`Proposed ${side === 'record' ? 'Record' : 'Field'} point for pair ${pair.id}`}
        value={value}
        className="font-mono"
        containerClassName="w-32 sm:w-40"
        onChange={(event) => correctPoint(pair.id, side, event.target.value)}
      >
        {!points.some((point) => point.point === value) && (
          <option value={value}>{value}</option>
        )}
        {points.map((point) => (
          <option key={point.point} value={point.point}>
            {point.point}
            {point.description ? ` — ${point.description}` : ''}
          </option>
        ))}
      </Select>
    );
  };

  const summary = (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge tone="accent" size="md">
        {pairs.length} proposed
      </Badge>
      <Badge size="md">{unmatchedRecord} unmatched Record</Badge>
      <Badge size="md">{unmatchedField} unmatched Field</Badge>
      <Badge size="md" tone={ambiguous.size ? 'warning' : 'neutral'}>
        {ambiguous.size} ambiguous
      </Badge>
      <Badge size="md" tone={reused.size ? 'warning' : 'neutral'}>
        {reused.size} reuse a point
      </Badge>
    </div>
  );

  const body = (
    <div className="space-y-4">
      {summary}
      {skippedRows > 0 && (
        <Alert tone="warning" role="alert">
          {skippedRows} pair-file {skippedRows === 1 ? 'row' : 'rows'} could not
          be matched and will be skipped.
        </Alert>
      )}
      {method === 'nearest' && (
        <Alert tone="warning" title="Check proximity matches">
          Proximity uses raw coordinates before alignment. Different origins or
          orientations can give misleading matches; inspect each proposal
          against field evidence.
        </Alert>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          controlSize="sm"
          leadingIcon={<Search />}
          placeholder="Search proposal"
          aria-label="Search proposal"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          containerClassName="min-w-0 basis-full sm:max-w-64 sm:flex-1 sm:basis-auto"
        />
        <SegmentedControl
          aria-label="Show"
          size="sm"
          value={view}
          onValueChange={setView}
          options={[
            { value: 'pairs', label: `Proposed pairs (${pairs.length})` },
            { value: 'unmatched', label: `Unmatched (${unmatched.length})` },
          ]}
        />
      </div>
      <div className="overflow-hidden rounded-lg border border-border">
        {view === 'pairs' ? (
          <Table
            stickyHeader
            aria-label="Proposed pairs"
            containerClassName="max-h-[min(24rem,50vh)]"
          >
            <THead>
              <Tr>
                <Th>Record</Th>
                <Th>Field</Th>
                <Th>Reason and review</Th>
              </Tr>
            </THead>
            <TBody>
              {shownPairs.map((pair) => (
                <Tr key={pair.id}>
                  <Td>{pointSelect(pair, 'record')}</Td>
                  <Td>{pointSelect(pair, 'field')}</Td>
                  <Td className="py-2 text-fg-muted">
                    <div>{reasonFor(pair)}</div>
                    {ambiguous.has(pair.id) && (
                      <div className="text-xs text-warning-fg">
                        Ambiguous — alternatives:{' '}
                        {candidates(pair.sys1Point)
                          .map((c) => c.point)
                          .join(', ')}
                      </div>
                    )}
                    {reused.has(pair.id) && (
                      <div className="text-xs text-warning-fg">
                        Point reused — review before solving
                      </div>
                    )}
                  </Td>
                </Tr>
              ))}
              {shownPairs.length === 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="px-3 py-8 text-center text-sm text-fg-muted"
                  >
                    No proposed pairs match this search.
                  </td>
                </tr>
              )}
            </TBody>
          </Table>
        ) : (
          <Table
            stickyHeader
            aria-label="Unmatched points"
            containerClassName="max-h-[min(24rem,50vh)]"
          >
            <THead>
              <Tr>
                <Th>Dataset</Th>
                <Th>Point</Th>
                <Th>Description</Th>
              </Tr>
            </THead>
            <TBody>
              {shownUnmatched.map((point) => (
                <Tr key={`${point.side}-${point.point}`}>
                  <Td className="text-fg-muted">{point.side}</Td>
                  <Td
                    mono
                    className={
                      point.side === 'Record'
                        ? 'font-medium text-record'
                        : 'text-field'
                    }
                  >
                    {point.point}
                  </Td>
                  <Td className="text-fg-muted">{point.description}</Td>
                </Tr>
              ))}
              {shownUnmatched.length === 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="px-3 py-8 text-center text-sm text-fg-muted"
                  >
                    {unmatched.length
                      ? 'No unmatched points match this search.'
                      : 'Every imported point is matched.'}
                  </td>
                </tr>
              )}
            </TBody>
          </Table>
        )}
      </div>
    </div>
  );

  const description =
    replacedCount > 0
      ? `Accepting replaces ${replacedCount} existing ${replacedCount === 1 ? 'pair' : 'pairs'} and their weights, and clears results. You can undo the replacement.`
      : editable
        ? 'Correct any Record or Field selection, then accept to create the pairs.'
        : 'Check the proposed correspondence, then accept to create the pairs.';

  const actions = (
    <>
      <Button onClick={onCancel}>Cancel matching</Button>
      <Button
        variant="primary"
        disabled={!pairs.length}
        onClick={() => onAccept(pairs)}
      >
        Accept {pairs.length} pairs
      </Button>
    </>
  );

  if (presentation === 'inline') {
    return (
      <Card role="region" aria-label="Review proposed matches">
        <CardHeader>
          <CardTitle as="h2" className="text-base">
            Review proposed matches
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardBody>{body}</CardBody>
        <CardFooter>{actions}</CardFooter>
      </Card>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent
        size="xl"
        className="min-w-0"
        title="Review proposed matches"
        description={description}
      >
        <DialogBody>{body}</DialogBody>
        <DialogFooter>{actions}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
